import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, Plus, Lock, Globe, Search, Crown, Shield, UserPlus, LogOut, Settings, MessageCircle, BadgeCheck, Trash2, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  "Technology", "Gaming", "Music", "Art", "Sports", "News", "Education",
  "Entertainment", "Science", "Business", "Health", "Food", "Travel", "Other"
];

const Communities = ({ user }) => {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [myCommunities, setMyCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCommunity, setNewCommunity] = useState({
    name: '',
    description: '',
    category: 'Other',
    is_private: false,
    rules: ['']
  });
  
  // Community detail state
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchCommunities();
    fetchMyCommunities();
  }, [searchQuery, selectedCategory]);

  const fetchCommunities = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API_URL}/communities?${params.toString()}`, {
        withCredentials: true,
      });
      setCommunities(response.data.communities || response.data);
    } catch (error) {
      toast.error('Failed to load communities');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCommunities = async () => {
    try {
      const response = await axios.get(`${API_URL}/communities/my`, {
        withCredentials: true,
      });
      setMyCommunities(response.data.communities || []);
    } catch (error) {
      console.error('Failed to load my communities');
    }
  };

  const createCommunity = async () => {
    if (!newCommunity.name.trim() || !newCommunity.description.trim()) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/communities`, {
        ...newCommunity,
        rules: newCommunity.rules.filter(r => r.trim())
      }, {
        withCredentials: true,
      });
      toast.success('Community created!');
      setCreateDialogOpen(false);
      setNewCommunity({ name: '', description: '', category: 'Other', is_private: false, rules: [''] });
      fetchCommunities();
      fetchMyCommunities();
      // Open the new community
      openCommunity(response.data.community_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create community');
    }
  };

  const joinCommunity = async (communityId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.post(`${API_URL}/communities/${communityId}/join`, {}, {
        withCredentials: true,
      });
      toast.success('Joined community!');
      fetchCommunities();
      fetchMyCommunities();
      if (selectedCommunity?.community_id === communityId) {
        openCommunity(communityId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join community');
    }
  };

  const leaveCommunity = async (communityId) => {
    try {
      await axios.post(`${API_URL}/communities/${communityId}/leave`, {}, {
        withCredentials: true,
      });
      toast.success('Left community');
      fetchCommunities();
      fetchMyCommunities();
      if (selectedCommunity?.community_id === communityId) {
        setSelectedCommunity(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave community');
    }
  };

  const openCommunity = async (communityId) => {
    setLoadingDetail(true);
    try {
      const [communityRes, postsRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/communities/${communityId}`, { withCredentials: true }),
        axios.get(`${API_URL}/communities/${communityId}/posts`, { withCredentials: true }),
        axios.get(`${API_URL}/communities/${communityId}/members?limit=10`, { withCredentials: true })
      ]);
      setSelectedCommunity(communityRes.data);
      setCommunityPosts(postsRes.data.posts || []);
      setCommunityMembers(membersRes.data.members || []);
    } catch (error) {
      toast.error('Failed to load community');
    } finally {
      setLoadingDetail(false);
    }
  };

  const createPost = async () => {
    if (!newPost.trim() || !selectedCommunity) return;
    try {
      const response = await axios.post(
        `${API_URL}/communities/${selectedCommunity.community_id}/posts`,
        { content: newPost },
        { withCredentials: true }
      );
      setCommunityPosts([response.data, ...communityPosts]);
      setNewPost('');
      toast.success('Post created!');
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(
        `${API_URL}/communities/${selectedCommunity.community_id}/posts/${postId}`,
        { withCredentials: true }
      );
      setCommunityPosts(communityPosts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const addRule = () => {
    if (newCommunity.rules.length < 10) {
      setNewCommunity({ ...newCommunity, rules: [...newCommunity.rules, ''] });
    }
  };

  const updateRule = (index, value) => {
    const updatedRules = [...newCommunity.rules];
    updatedRules[index] = value;
    setNewCommunity({ ...newCommunity, rules: updatedRules });
  };

  const removeRule = (index) => {
    const updatedRules = newCommunity.rules.filter((_, i) => i !== index);
    setNewCommunity({ ...newCommunity, rules: updatedRules.length ? updatedRules : [''] });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'moderator': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return null;
    }
  };

  // Community Detail View
  if (selectedCommunity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="community-detail-page">
        <div className="max-w-4xl mx-auto px-6 pt-8">
          {/* Back button */}
          <button
            onClick={() => setSelectedCommunity(null)}
            className="mb-4 text-gray-600 dark:text-gray-400 hover:text-primary flex items-center gap-1"
          >
            ← Back to Communities
          </button>
          
          {/* Community Header */}
          <Card className="rounded-3xl overflow-hidden border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <div className="h-32 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
              {selectedCommunity.cover_photo && (
                <img src={selectedCommunity.cover_photo} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="px-6 pb-6">
              <div className="flex items-end justify-between -mt-10 mb-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-3xl text-white font-bold border-4 border-white dark:border-gray-800 shadow-xl">
                  {selectedCommunity.avatar ? (
                    <img src={selectedCommunity.avatar} alt="" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    selectedCommunity.name?.charAt(0)
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedCommunity.is_member ? (
                    <>
                      {selectedCommunity.my_role === 'admin' && (
                        <Button variant="outline" size="sm" className="rounded-full">
                          <Settings className="w-4 h-4 mr-1" />
                          Settings
                        </Button>
                      )}
                      <Button
                        onClick={() => leaveCommunity(selectedCommunity.community_id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full text-red-500 border-red-500 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Leave
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => joinCommunity(selectedCommunity.community_id)}
                      className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Join Community
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-heading font-black text-gray-900 dark:text-white">
                  {selectedCommunity.name}
                </h1>
                {selectedCommunity.is_private ? (
                  <Lock className="w-5 h-5 text-gray-500" />
                ) : (
                  <Globe className="w-5 h-5 text-gray-500" />
                )}
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{selectedCommunity.description}</p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                  <Users className="w-4 h-4" />
                  <span className="font-semibold">{selectedCommunity.members_count}</span> members
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">{selectedCommunity.posts_count}</span> posts
                </div>
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-semibold">
                  {selectedCommunity.category}
                </span>
              </div>
            </div>
          </Card>
          
          <div className="grid grid-cols-3 gap-6">
            {/* Posts Section */}
            <div className="col-span-2 space-y-4">
              {/* Create Post */}
              {selectedCommunity.is_member && (
                <Card className="p-4 rounded-2xl border border-white/20 shadow-lg bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="Share something with the community..."
                    className="rounded-xl border-2 resize-none mb-3"
                    rows={3}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={createPost}
                      disabled={!newPost.trim()}
                      className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold"
                    >
                      Post
                    </Button>
                  </div>
                </Card>
              )}
              
              {/* Posts List */}
              {communityPosts.length === 0 ? (
                <Card className="p-8 rounded-2xl border border-white/20 shadow-lg bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No posts yet. Be the first to post!</p>
                </Card>
              ) : (
                communityPosts.map((post, idx) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card className="p-4 rounded-2xl border border-white/20 shadow-lg bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={post.user?.avatar} />
                          <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                            {post.user?.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900 dark:text-white">{post.user?.name}</span>
                              {post.user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                              <span className="text-sm text-gray-500">
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            {(post.user_id === user?.user_id || selectedCommunity.my_role === 'admin' || selectedCommunity.my_role === 'moderator') && (
                              <button
                                onClick={() => deletePost(post.post_id)}
                                className="p-1 text-gray-400 hover:text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 mt-2">{post.content}</p>
                          {post.video_url && (
                            <div className="relative rounded-xl overflow-hidden bg-black mt-2">
                              <video
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                                className="w-full max-h-48 object-contain"
                                controls
                              />
                            </div>
                          )}
                          {post.images && post.images.length > 0 && (
                            <div className={`grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} mt-2`}>
                              {post.images.map((img, i) => (
                                <img key={i} src={img} alt="" className="rounded-xl w-full object-cover max-h-48" />
                              ))}
                            </div>
                          )}
                          {post.audio_url && (
                            <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                              <audio
                                src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                                controls
                                className="w-full h-10"
                                preload="metadata"
                              />
                            </div>
                          )}
                          {post.image_url && (!post.images || post.images.length === 0) && (
                            <img src={post.image_url} alt="" className="mt-2 rounded-xl w-full object-cover max-h-48" />
                          )}
                          {post.gif_url && (
                            <img src={post.gif_url} alt="" className="mt-2 max-h-48 rounded-xl" />
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
            
            {/* Sidebar */}
            <div className="space-y-4">
              {/* Rules */}
              {selectedCommunity.rules?.length > 0 && (
                <Card className="p-4 rounded-2xl border border-white/20 shadow-lg bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3">Community Rules</h3>
                  <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {selectedCommunity.rules.map((rule, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="font-semibold text-primary">{idx + 1}.</span>
                        {rule}
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
              
              {/* Members */}
              <Card className="p-4 rounded-2xl border border-white/20 shadow-lg bg-white/70 dark:bg-gray-800/70 backdrop-blur-md">
                <h3 className="font-bold text-gray-900 dark:text-white mb-3">Members</h3>
                <div className="space-y-2">
                  {communityMembers.map((member) => (
                    <div key={member.member_id} className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={member.user?.avatar} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xs font-bold">
                          {member.user?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{member.user?.name}</span>
                      {getRoleIcon(member.role)}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="communities-page">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white" data-testid="communities-title">
                Communities
              </h1>
            </div>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold px-6 shadow-lg"
                  data-testid="create-community-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Community
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-heading font-bold">Create Community</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Community Name *</label>
                    <Input
                      placeholder="Enter community name"
                      value={newCommunity.name}
                      onChange={(e) => setNewCommunity({ ...newCommunity, name: e.target.value })}
                      className="rounded-xl border-2 h-12"
                      data-testid="community-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description *</label>
                    <Textarea
                      placeholder="What's this community about?"
                      value={newCommunity.description}
                      onChange={(e) => setNewCommunity({ ...newCommunity, description: e.target.value })}
                      className="rounded-xl border-2 resize-none"
                      rows={3}
                      data-testid="community-description-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category</label>
                    <select
                      value={newCommunity.category}
                      onChange={(e) => setNewCommunity({ ...newCommunity, category: e.target.value })}
                      className="w-full h-12 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Community Rules (optional)</label>
                    {newCommunity.rules.map((rule, idx) => (
                      <div key={idx} className="flex gap-2 mb-2">
                        <Input
                          placeholder={`Rule ${idx + 1}`}
                          value={rule}
                          onChange={(e) => updateRule(idx, e.target.value)}
                          className="rounded-xl border-2 h-10 flex-1"
                        />
                        {newCommunity.rules.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeRule(idx)} className="text-red-500">
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                    {newCommunity.rules.length < 10 && (
                      <Button variant="ghost" size="sm" onClick={addRule} className="text-primary">
                        + Add Rule
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="private"
                      checked={newCommunity.is_private}
                      onChange={(e) => setNewCommunity({ ...newCommunity, is_private: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="private" className="text-sm text-gray-700 dark:text-gray-300">
                      Make this community private (members only)
                    </label>
                  </div>
                  <Button
                    onClick={createCommunity}
                    className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg"
                    data-testid="create-submit-btn"
                  >
                    Create Community
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Search & Filter */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search communities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full border-2"
              />
            </div>
          </div>
          
          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap mb-6">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                !selectedCategory 
                  ? 'bg-primary text-white' 
                  : 'bg-white/70 dark:bg-gray-800/70 text-gray-700 dark:text-gray-300 hover:bg-primary/10'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  selectedCategory === cat 
                    ? 'bg-primary text-white' 
                    : 'bg-white/70 dark:bg-gray-800/70 text-gray-700 dark:text-gray-300 hover:bg-primary/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-2xl p-1 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-6">
            <TabsTrigger value="discover" className="rounded-xl font-bold">
              <Globe className="w-4 h-4 mr-2" />
              Discover
            </TabsTrigger>
            <TabsTrigger value="my" className="rounded-xl font-bold">
              <Users className="w-4 h-4 mr-2" />
              My Communities ({myCommunities.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="discover">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : communities.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 dark:text-gray-400">No communities found. Create the first one!</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {communities.map((community, idx) => (
                  <motion.div
                    key={community.community_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card 
                      className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer" 
                      onClick={() => openCommunity(community.community_id)}
                      data-testid={`community-${idx}`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-2xl text-white font-bold">
                          {community.avatar ? (
                            <img src={community.avatar} alt="" className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            community.name?.charAt(0)
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2 truncate">
                            {community.name}
                            {community.is_private && <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                          </h3>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-400">
                            {community.category}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">{community.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{community.members_count}</span>
                          </div>
                          <span>{community.posts_count} posts</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="my">
            {myCommunities.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 dark:text-gray-400">You haven't joined any communities yet.</p>
                <Button
                  onClick={() => setActiveTab('discover')}
                  className="mt-4 rounded-full bg-primary hover:bg-primary-700 text-white font-bold"
                >
                  Discover Communities
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myCommunities.map((community, idx) => (
                  <motion.div
                    key={community.community_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card 
                      className="p-6 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer" 
                      onClick={() => openCommunity(community.community_id)}
                      data-testid={`my-community-${idx}`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-2xl text-white font-bold">
                          {community.avatar ? (
                            <img src={community.avatar} alt="" className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            community.name?.charAt(0)
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2 truncate">
                            {community.name}
                            {getRoleIcon(community.my_role)}
                          </h3>
                          <span className="text-xs text-gray-500">{community.my_role}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">{community.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{community.members_count}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Communities;
